import {useTableSort} from '@flexinet/ui-components';
import {useBindingDriftReportsInfiniteQuery} from './useBindingDriftReportsQuery';
import {defaultSortStatus} from '../config';
import {BindingDriftReportSummary} from '../types';

// The high-level list hook: query + sort. There is no keyword search and no filter
// — see the note at RecordsTable.
export const useBindingDriftReports = () => {
    const query = useBindingDriftReportsInfiniteQuery();

    // Type guard, not a bare flatMap: a page that came back without `items` (a
    // GraphQL-level error the service did not catch) contributes nothing rather than
    // an undefined row.
    const items: BindingDriftReportSummary[] = (query.data?.pages?.flatMap(
        page => (page && 'items' in page) ? page.items : []
    ) ?? []).filter(Boolean);

    // Sort state is useTableSort's own useState — component-level, not a module
    // store: it is how one operator is looking at the table right now.
    //
    // ⚠️ Client-side, over the pages LOADED SO FAR. Token pagination cannot reorder
    // rows the browser has not fetched; that is a property of the model, not a bug.
    // The default (newest runStartedAt first) matches the API's own order, so it
    // only changes anything once the operator clicks a header.
    const {sortStatus, setSortStatus, sorted} = useTableSort<BindingDriftReportSummary>(
        items,
        defaultSortStatus,
    );

    // The last page's real token. Opaque: passed through, never parsed or built.
    const pages = query.data?.pages ?? [];
    const nextToken = query.hasNextPage ? (pages[pages.length - 1]?.nextToken ?? null) : null;

    return {
        items: sorted,
        sortStatus,
        setSortStatus,
        // First load, and a Reload refetch — but not "load more", which has its own
        // indicator in the footer. Without the refetch half, Reload was a dead click.
        loading: query.isLoading || (query.isFetching && !query.isFetchingNextPage),
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        nextToken,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => {
            query.refetch();
        },
    };
};
